import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Avatar,
  TablePagination,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";

interface BookingResponse {
  id: number;
  title?: string;
  service?: string;
  package_item?: { name: string };
  services?: Array<{ date_of_service: string }>;
  deceased?: Array<{
    first_name?: string;
    surname?: string;
    person_image?: string;
  }>;
}

interface BookingsCardProps {
  bookings: BookingResponse[];
  totalBookings: number;
  bookingsLoading: boolean;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBookingClick: (bookingId: number) => void;
  formatDate: (date: string | undefined) => string;
  InfoCard: React.ComponentType<{
    title: string;
    icon: React.ReactNode;
    sx?: object;
    children: React.ReactNode;
  }>;
}

export const BookingsCardV2: React.FC<BookingsCardProps> = ({
  bookings,
  totalBookings,
  bookingsLoading,
  page,
  rowsPerPage,
  onRowsPerPageChange,
  onPageChange,
  onBookingClick,
  formatDate,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  return (
    <Box sx={{ width: "100%", fontFamily: "inherit" }}>
      {bookingsLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : bookings.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ textAlign: "center", py: 4, color: "text.secondary" }}
        >
          No Related Bookings
        </Typography>
      ) : (
        <>
          {/* Table */}
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
              }}
            >
              {/* Header */}
              <thead>
                <tr>
                  {["Name", "Service", "Package", "Room", "Date"].map((col) => (
                    <th
                      key={col}
                      style={{
                        backgroundColor: reduxTheme.primary_colors[400],
                        color: "white",
                        padding: "10px 14px",
                        textAlign: "left",
                        fontWeight: 500,
                        fontSize: 13,
                        letterSpacing: 0.2,
                      }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Body */}
              <tbody>
                {bookings.map((booking, index) => {
                  const deceased = booking?.deceased?.[0];
                  const name =
                    deceased?.first_name && deceased?.surname
                      ? `${deceased.first_name} ${deceased.surname}`
                      : (booking?.title ?? "—");

                  const date = formatDate(
                    booking.services?.[0]?.date_of_service,
                  );

                  return (
                    <tr
                      key={booking.id}
                      onClick={() => onBookingClick(booking.id)}
                      style={{
                        backgroundColor:
                          index % 2 === 0
                            ? reduxTheme.primary_colors[900]
                            : reduxTheme.primary_colors[800],
                        cursor: "pointer",
                        transition: "background-color 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        (
                          e.currentTarget as HTMLTableRowElement
                        ).style.backgroundColor =
                          reduxTheme.primary_colors[800];
                      }}
                      onMouseLeave={(e) => {
                        (
                          e.currentTarget as HTMLTableRowElement
                        ).style.backgroundColor =
                          index % 2 === 0
                            ? reduxTheme.primary_colors[900]
                            : reduxTheme.primary_colors[800];
                      }}
                    >
                      {/* Name */}
                      <td style={{ padding: "8px 14px" }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Avatar
                            src={deceased?.person_image || ""}
                            sx={{
                              width: 28,
                              height: 28,
                              fontSize: 12,
                              bgcolor: reduxTheme.primary_colors[300],
                            }}
                          >
                            {(deceased?.first_name?.[0] || "").toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" fontSize={13}>
                            {name}
                          </Typography>
                        </Box>
                      </td>

                      {/* Service */}
                      <td style={{ padding: "8px 14px" }}>
                        <Typography variant="body2" fontSize={13}>
                          {booking.service ?? "—"}
                        </Typography>
                      </td>

                      {/* Package */}
                      <td style={{ padding: "8px 14px" }}>
                        <Typography variant="body2" fontSize={13}>
                          {booking.package_item?.name ?? "—"}
                        </Typography>
                      </td>

                      {/* Room */}
                      <td style={{ padding: "8px 14px" }}>
                        {/* Room */}
                        <td style={{ padding: "8px 14px" }}>
                          <Typography variant="body2" fontSize={13}>
                            —
                          </Typography>
                        </td>
                      </td>

                      {/* Date */}
                      <td style={{ padding: "8px 14px" }}>
                        <Typography variant="body2" fontSize={13}>
                          {date}
                        </Typography>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>

          <TablePagination
            component="div"
            count={totalBookings}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </>
      )}
    </Box>
  );
};

export default BookingsCardV2;
